import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  Building2, 
  Check, 
  X, 
  AlertCircle,
  UserPlus
} from 'lucide-react'
import { getFirebaseDb, getFirebaseAuth, getGoogleProvider, getCallableFunction } from '@/lib/firebase'
import { signInWithPopup } from 'firebase/auth'
import { useAuth } from '@/contexts/AuthContext'
import { 
  query, 
  where, 
  getDocs, 
  collectionGroup
} from 'firebase/firestore'

interface InviteData {
  id: string
  email: string
  role: string
  status: string
  organizationId: string
  organizationName: string
  expiresAt: Date
  invitedByEmail?: string
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [invite, setInvite] = useState<InviteData | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [signingIn, setSigningIn] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) {
        setError('Invalid invite link')
        setLoading(false)
        return
      }

      // Wait for auth to complete before fetching
      if (authLoading) {
        return
      }

      // If user is not signed in, don't try to fetch (will fail due to rules)
      // Just show the sign-in prompt
      if (!user) {
        setLoading(false)
        return
      }

      try {
        const db = getFirebaseDb()
        
        // Query all invites across all orgs to find this token
        // Must include email filter for security rules to allow the collection group query
        const userEmail = user.email?.toLowerCase()
        if (!userEmail) {
          setError('Your account does not have an email address')
          setLoading(false)
          return
        }
        
        const invitesQuery = query(
          collectionGroup(db, 'invites'),
          where('token', '==', token),
          where('email', '==', userEmail)
        )
        const snapshot = await getDocs(invitesQuery)
        
        if (snapshot.empty) {
          setError('Invite not found, has been deleted, or was sent to a different email address')
          setLoading(false)
          return
        }
        
        const inviteDoc = snapshot.docs[0]
        const data = inviteDoc.data()
        const expiresAt = data.expiresAt?.toDate() || new Date()
        
        // Check if expired
        if (expiresAt < new Date()) {
          setError('This invite has expired')
          setLoading(false)
          return
        }
        
        // Check if already accepted
        if (data.status === 'accepted') {
          setError('This invite has already been used')
          setLoading(false)
          return
        }
        
        setInvite({
          id: inviteDoc.id,
          email: data.email,
          role: data.role,
          status: data.status,
          organizationId: data.organizationId,
          organizationName: data.organizationName,
          expiresAt,
          invitedByEmail: data.invitedByEmail
        })
      } catch (error) {
        console.error('Error fetching invite:', error)
        setError('Failed to load invite')
      } finally {
        setLoading(false)
      }
    }

    fetchInvite()
  }, [token, user, authLoading])

  const handleAcceptInvite = async () => {
    if (!invite || !user) return
    
    // Check if user email matches invite email
    if (user.email?.toLowerCase() !== invite.email.toLowerCase()) {
      setError(`This invite was sent to ${invite.email}. Please sign in with that email address.`)
      return
    }
    
    setAccepting(true)
    setError(null)
    
    try {
      // Call the Cloud Function to accept the invite
      // This ensures only the server can create member records
      const acceptInviteFn = getCallableFunction<
        { organizationId: string; inviteId: string },
        { success: boolean; message: string }
      >('acceptInvite')
      
      await acceptInviteFn({
        organizationId: invite.organizationId,
        inviteId: invite.id
      })
      
      setSuccess(true)
      
      // Redirect to organization after a delay
      setTimeout(() => {
        navigate(`/organizations`)
      }, 2000)
    } catch (error: unknown) {
      console.error('Error accepting invite:', error)
      // Extract error message from Firebase Functions error
      const firebaseError = error as { code?: string; message?: string }
      if (firebaseError.message) {
        setError(firebaseError.message)
      } else {
        setError('Failed to accept invite. Please try again.')
      }
    } finally {
      setAccepting(false)
    }
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <X className="h-6 w-6 text-destructive" />
              </div>
              <h2 className="mt-4 text-xl font-semibold">Invalid Invite</h2>
              <p className="mt-2 text-muted-foreground">{error}</p>
              <Button className="mt-6" asChild>
                <Link to="/organizations">Go to Organizations</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Show sign-in screen for unauthenticated users
  if (!user && !loading) {
    const handleSignIn = async () => {
      setSigningIn(true)
      try {
        const auth = getFirebaseAuth()
        const provider = getGoogleProvider()
        await signInWithPopup(auth, provider)
        // After sign-in, the useEffect will re-run and fetch the invite
      } catch (error) {
        console.error('Sign in error:', error)
        setError('Failed to sign in. Please try again.')
      } finally {
        setSigningIn(false)
      }
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>You're Invited!</CardTitle>
            <CardDescription>
              Sign in to view and accept your invitation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">
                You've been invited to join an organization on Session.
                Sign in to view the details and accept.
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                <span className="text-sm text-destructive">{error}</span>
              </div>
            )}

            <Button 
              className="w-full" 
              onClick={handleSignIn}
              disabled={signingIn}
            >
              {signingIn ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Check className="h-6 w-6 text-green-500" />
              </div>
              <h2 className="mt-4 text-xl font-semibold">Welcome to {invite?.organizationName}!</h2>
              <p className="mt-2 text-muted-foreground">
                You've successfully joined the organization. Redirecting...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>You're Invited!</CardTitle>
          <CardDescription>
            You've been invited to join an organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Organization</span>
              <span className="font-medium">{invite?.organizationName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Role</span>
              <span className="font-medium capitalize">{invite?.role}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Invited by</span>
              <span className="font-medium">{invite?.invitedByEmail || 'Unknown'}</span>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          <div className="space-y-3">
            <p className="text-sm text-center text-muted-foreground">
              Signed in as <strong>{user?.email}</strong>
            </p>
            <Button 
              className="w-full" 
              onClick={handleAcceptInvite}
              disabled={accepting}
            >
              {accepting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Accepting...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Accept Invite
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
